import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { monitorForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { AddFieldMenu } from './AddFieldMenu';
import { FormFieldEditorRow, type BuilderField } from './FormFieldEditorRow';
import {
  serializeChoiceOptions,
  serializeLinearScaleOptions,
  serializeGridOptions,
} from '../../adapters/field-type.adapter';
import type { FormFieldType } from '../../types';

interface FormFieldsEditorProps {
  fields: BuilderField[];
  onChange: (fields: BuilderField[]) => void;
  readOnly?: boolean;
}

function defaultOptionsFor(fieldType: FormFieldType): string | null {
  switch (fieldType) {
    case 'Select':
    case 'MultiSelect':
      return serializeChoiceOptions(['Opción 1', 'Opción 2']);
    case 'LinearScale':
      return serializeLinearScaleOptions({ min: 1, max: 5 });
    case 'Grid':
      return serializeGridOptions({ rows: ['Fila 1'], columns: ['Columna 1'] });
    default:
      return null;
  }
}

function createBuilderField(fieldType: FormFieldType, order: number): BuilderField {
  return {
    localId: crypto.randomUUID(),
    id: null,
    fieldType,
    label: '',
    required: false,
    order,
    options: defaultOptionsFor(fieldType),
    validation: null,
  };
}

export function FormFieldsEditor({ fields, onChange, readOnly = false }: FormFieldsEditorProps) {
  const { t } = useTranslation('forms');

  useEffect(() => {
    if (readOnly) return undefined;
    return monitorForElements({
      onDrop({ source, location }) {
        const target = location.current.dropTargets[0];
        if (!target) return;

        const sourceLocalId = (source.data as { localId?: string }).localId;
        const targetLocalId = (target.data as { localId?: string }).localId;
        if (!sourceLocalId || !targetLocalId || sourceLocalId === targetLocalId) return;

        const sourceIndex = fields.findIndex((f) => f.localId === sourceLocalId);
        const targetIndex = fields.findIndex((f) => f.localId === targetLocalId);
        if (sourceIndex === -1 || targetIndex === -1) return;

        const reordered = [...fields];
        const [moved] = reordered.splice(sourceIndex, 1);
        reordered.splice(targetIndex, 0, moved);
        onChange(reordered.map((f, i) => ({ ...f, order: i })));
      },
    });
  }, [fields, onChange, readOnly]);

  function handleAddField(fieldType: FormFieldType) {
    onChange([...fields, createBuilderField(fieldType, fields.length)]);
  }

  function handleFieldChange(localId: string, updated: BuilderField) {
    onChange(fields.map((f) => (f.localId === localId ? updated : f)));
  }

  function handleFieldDelete(localId: string) {
    onChange(fields.filter((f) => f.localId !== localId).map((f, i) => ({ ...f, order: i })));
  }

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-base font-semibold md:text-lg">{t('builder.fieldsTitle')}</h2>
        <p className="text-xs text-muted-foreground mt-1">{t('builder.fieldsHint')}</p>
      </div>

      {fields.length === 0 ? (
        <div className="py-4 text-center" data-testid="forms-builder-fields-empty">
          <p className="text-sm font-medium">{t('builder.emptyFields')}</p>
          {!readOnly && (
            <p className="text-xs text-muted-foreground mt-1">{t('builder.emptyFieldsHint')}</p>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          {fields
            .slice()
            .sort((a, b) => a.order - b.order)
            .map((field) => (
              <FormFieldEditorRow
                key={field.localId}
                field={field}
                readOnly={readOnly}
                onChange={(updated) => handleFieldChange(field.localId, updated)}
                onDelete={() => handleFieldDelete(field.localId)}
              />
            ))}
        </div>
      )}

      {!readOnly && <AddFieldMenu onSelect={handleAddField} />}
    </div>
  );
}
